import { useEffect, useLayoutEffect } from "react";
import { Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import Landing from "./pages/Landing";
import Onboarding from "./pages/Onboarding";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Pay from "./pages/Pay";
import PayerCreate from "./pages/PayerCreate";
import Trust from "./pages/Trust";
import Privacy from "./pages/legal/Privacy";
import Terms from "./pages/legal/Terms";
import DeleteData from "./pages/legal/DeleteData";
import { SignOff } from "./lib/SignOff";

/**
 * A single-page app keeps the previous page's scroll across navigations. This puts new pages at
 * the top, takes a `#section` link to its section, and on back/forward restores where that page
 * was left.
 */
const savedScroll = new Map<string, number>();
// The manager's own scrollTo fires scroll events too; until they've settled,
// the page being left must not record them as where the reader was.
let ignoreScrollUntil = 0;
function moveTo(top: number) {
  ignoreScrollUntil = performance.now() + 150;
  window.scrollTo({ top, left: 0, behavior: "instant" });
}

function ScrollManager() {
  const { pathname, hash, key } = useLocation();
  const navType = useNavigationType();

  // Remember where each history entry was scrolled to...
  useEffect(() => {
    const onScroll = () => {
      if (performance.now() < ignoreScrollUntil) return;
      savedScroll.set(key, window.scrollY);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [key]);

  useLayoutEffect(() => {
    const section = hash ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;
    if (navType === "POP") {
      // A #hash the router didn't create (typed in the address bar, or a plain <a href="#x">) arrives
      // as a POP with the placeholder key "default"; restoring a remembered position there would drag
      // the page back from the section it was just taken to, so go to the section.
      if (key === "default" && section) {
        section.scrollIntoView();
        return;
      }
      // Real back/forward: put the page back where that entry was left.
      const y = savedScroll.get(key);
      if (y !== undefined) moveTo(y);
      return;
    }
    if (section) {
      section.scrollIntoView();
      return;
    }
    moveTo(0); // "instant" inside: the stylesheet smooth-scrolls in-page anchors
  }, [pathname, hash, key, navType]);

  return null;
}

export default function App() {
  return (
    <>
      <ScrollManager />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/login" element={<Login />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/pay/:id" element={<Pay />} />
        <Route path="/trust" element={<Trust />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/delete-data" element={<DeleteData />} />
        {/* An org's public link, confirmpay.link/<slug>: one dynamic segment, so only paths that match
                    no static route above fall through to it. */}
        <Route path="/:orgSlug" element={<PayerCreate />} />
        <Route path="*" element={<Landing />} />
      </Routes>
      <SignOff />
    </>
  );
}
